import type { FeaturedRegionPropertyTypeLink } from './propertyTypeLinks';
import type { CarouselApi } from '@/components/ui/carousel';
import type { FeaturedRegionTagAvailability } from '~/utils/regionTagAvailability';
import type { Region } from '~~/core/entities/Region';
import type { RegionReport } from '~~/core/ports/repositories';
import type { Ref } from 'vue';

import {
	getFeaturedRegionTagKey,
	mapRegionReportsToFeaturedRegionTagAvailability
} from '~/utils/regionTagAvailability';

import { getFeaturedRegionPropertyTypeLinks } from './propertyTypeLinks';

type CarouselApiWithSlidesInView = NonNullable<CarouselApi> & {
	slidesInView?: () => number[];
};

interface UseFeaturedRegionTagsOptions {
	activeRegions: Ref<Region[]>;
	currentCarouselItemIndex: Ref<number>;
	getCarouselApi: () => CarouselApi | undefined;
}

const INITIAL_VISIBLE_REGION_TAGS_COUNT = 3;
const REGION_TAGS_REQUEST_IDLE_TIMEOUT = 1200;

export function useFeaturedRegionTags({
	activeRegions,
	currentCarouselItemIndex,
	getCarouselApi
}: UseFeaturedRegionTagsOptions) {
	const regionTagAvailabilityByKey = shallowRef<
		Record<string, FeaturedRegionTagAvailability>
	>({});

	const requestedRegionTagKeys = new Set<string>();
	const pendingRegionTagKeys = new Set<string>();
	let carouselRegionTagsApi: CarouselApi | undefined;
	let isVisibleRegionTagsRequestScheduled = false;
	let visibleRegionTagsIdleCallbackId: number | null = null;
	let visibleRegionTagsTimeoutId: ReturnType<typeof setTimeout> | null = null;

	function getRegionWithTagAvailability(region: Region): Region {
		const availability =
			regionTagAvailabilityByKey.value[getFeaturedRegionTagKey(region)];

		if (!availability) return region;

		return {
			...region,
			...availability
		};
	}

	// Memoiza os links por região. O template chama getRegionPropertyTypeLinks
	// dentro de um v-for, então sem memo a lista era recalculada para todos os
	// cards a cada re-render (ex.: hover altera hoveredRegionTitle), inflando o
	// INP. Este computed só recalcula quando activeRegions ou a disponibilidade
	// de tags muda; o template passa a fazer apenas um lookup O(1).
	const regionPropertyTypeLinksByKey = computed(() => {
		const linksByKey = new Map<string, FeaturedRegionPropertyTypeLink[]>();

		for (const region of activeRegions.value) {
			linksByKey.set(
				getFeaturedRegionTagKey(region),
				getFeaturedRegionPropertyTypeLinks(getRegionWithTagAvailability(region))
			);
		}

		return linksByKey;
	});

	function getRegionPropertyTypeLinks(region: Region) {
		return (
			regionPropertyTypeLinksByKey.value.get(getFeaturedRegionTagKey(region)) ??
			getFeaturedRegionPropertyTypeLinks(getRegionWithTagAvailability(region))
		);
	}

	function getFallbackVisibleRegionIndexes(): number[] {
		const firstVisibleIndex = Math.min(
			currentCarouselItemIndex.value,
			Math.max(activeRegions.value.length - 1, 0)
		);
		const visibleCount = Math.min(
			INITIAL_VISIBLE_REGION_TAGS_COUNT,
			Math.max(activeRegions.value.length - firstVisibleIndex, 0)
		);

		return Array.from(
			{ length: visibleCount },
			(_, index) => firstVisibleIndex + index
		);
	}

	function getVisibleRegions(carouselApi?: CarouselApi): Region[] {
		const visibleIndexes: number[] =
			(
				carouselApi as CarouselApiWithSlidesInView | undefined
			)?.slidesInView?.() || [];
		const indexes = visibleIndexes.length
			? visibleIndexes
			: getFallbackVisibleRegionIndexes();
		const seenRegionKeys = new Set<string>();

		return indexes.reduce<Region[]>((regions, index) => {
			const region = activeRegions.value[index];

			if (!region) return regions;

			const regionKey = getFeaturedRegionTagKey(region);

			if (seenRegionKeys.has(regionKey)) return regions;

			seenRegionKeys.add(regionKey);
			regions.push(region);

			return regions;
		}, []);
	}

	function scheduleVisibleRegionTagsRequest(carouselApi?: CarouselApi) {
		if (!import.meta.client || isVisibleRegionTagsRequestScheduled) return;

		isVisibleRegionTagsRequestScheduled = true;

		const runRequest = () => {
			visibleRegionTagsIdleCallbackId = null;
			visibleRegionTagsTimeoutId = null;
			isVisibleRegionTagsRequestScheduled = false;

			void fetchVisibleRegionTags(
				carouselApi || carouselRegionTagsApi || getCarouselApi()
			);
		};

		if ('requestIdleCallback' in window) {
			visibleRegionTagsIdleCallbackId = window.requestIdleCallback(runRequest, {
				timeout: REGION_TAGS_REQUEST_IDLE_TIMEOUT
			});

			return;
		}

		visibleRegionTagsTimeoutId = setTimeout(runRequest, 0);
	}

	async function fetchVisibleRegionTags(carouselApi?: CarouselApi) {
		const visibleRegions = getVisibleRegions(carouselApi);
		const regionsToRequest = visibleRegions.filter(region => {
			const regionKey = getFeaturedRegionTagKey(region);

			return (
				!requestedRegionTagKeys.has(regionKey) &&
				!pendingRegionTagKeys.has(regionKey)
			);
		});

		if (!regionsToRequest.length) return;

		const regionKeys = regionsToRequest.map(region =>
			getFeaturedRegionTagKey(region)
		);
		const regionsByLocation = regionsToRequest.reduce((groups, region) => {
			const locationKey = `${region.city}|${region.state}`;
			const currentRegions = groups.get(locationKey) || [];

			currentRegions.push(region);
			groups.set(locationKey, currentRegions);

			return groups;
		}, new Map<string, Region[]>());

		regionKeys.forEach(regionKey => pendingRegionTagKeys.add(regionKey));

		try {
			const nextAvailability: Record<string, FeaturedRegionTagAvailability> =
				{};
			const fulfilledRegionKeys = new Set<string>();

			await Promise.all(
				Array.from(regionsByLocation.values()).map(async groupedRegions => {
					const [firstRegion] = groupedRegions;

					if (!firstRegion?.city || !firstRegion.state) return;

					try {
						const reports = await $fetch<RegionReport[]>('/api/region/report', {
							method: 'GET',
							query: {
								regions: [
									...new Set(groupedRegions.map(region => region.name))
								],
								city: firstRegion.city,
								state: firstRegion.state,
								includeTypologies: true
							}
						});
						const groupAvailability =
							mapRegionReportsToFeaturedRegionTagAvailability(
								groupedRegions,
								reports || []
							);

						Object.assign(nextAvailability, groupAvailability);
						groupedRegions.forEach(region =>
							fulfilledRegionKeys.add(getFeaturedRegionTagKey(region))
						);
					} catch {
						// Keep fallback tags visible and allow this location group to retry.
					}
				})
			);

			if (Object.keys(nextAvailability).length) {
				regionTagAvailabilityByKey.value = {
					...regionTagAvailabilityByKey.value,
					...nextAvailability
				};
			}

			fulfilledRegionKeys.forEach(regionKey =>
				requestedRegionTagKeys.add(regionKey)
			);
		} catch {
			// Keep fallback tags visible and allow a future carousel event to retry.
		} finally {
			regionKeys.forEach(regionKey => pendingRegionTagKeys.delete(regionKey));
		}
	}

	function handleCarouselVisibleSlidesChange(carouselApi: CarouselApi) {
		scheduleVisibleRegionTagsRequest(carouselApi);
	}

	function bindCarouselVisibleRegionTagRequests(carouselApi?: CarouselApi) {
		if (!carouselApi || carouselRegionTagsApi === carouselApi) return;

		unbindCarouselVisibleRegionTagRequests();

		carouselRegionTagsApi = carouselApi;
		carouselApi.on('select', handleCarouselVisibleSlidesChange);
		carouselApi.on('reInit', handleCarouselVisibleSlidesChange);
		carouselApi.on('slidesInView', handleCarouselVisibleSlidesChange);
	}

	function unbindCarouselVisibleRegionTagRequests() {
		if (!carouselRegionTagsApi) return;

		carouselRegionTagsApi.off('select', handleCarouselVisibleSlidesChange);
		carouselRegionTagsApi.off('reInit', handleCarouselVisibleSlidesChange);
		carouselRegionTagsApi.off(
			'slidesInView',
			handleCarouselVisibleSlidesChange
		);
		carouselRegionTagsApi = undefined;
	}

	function clearScheduledVisibleRegionTagsRequest() {
		if (
			import.meta.client &&
			visibleRegionTagsIdleCallbackId !== null &&
			'cancelIdleCallback' in window
		) {
			window.cancelIdleCallback(visibleRegionTagsIdleCallbackId);
		}

		if (visibleRegionTagsTimeoutId !== null) {
			clearTimeout(visibleRegionTagsTimeoutId);
		}

		visibleRegionTagsIdleCallbackId = null;
		visibleRegionTagsTimeoutId = null;
		isVisibleRegionTagsRequestScheduled = false;
	}

	watch(
		activeRegions,
		() => {
			nextTick(() => scheduleVisibleRegionTagsRequest());
		},
		{ flush: 'post' }
	);

	onBeforeUnmount(() => {
		unbindCarouselVisibleRegionTagRequests();
		clearScheduledVisibleRegionTagsRequest();
	});

	return {
		bindCarouselVisibleRegionTagRequests,
		getRegionPropertyTypeLinks,
		scheduleVisibleRegionTagsRequest
	};
}
